import React, { useState } from 'react';
import { Upload, Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';
import { ApiResponse } from 'types';
import { ImportResult } from 'api/services/traiCompliance.service';
import { readFileText, Tone } from './traiRules';

export const muted = { color: 'var(--text-muted)' } as const;
export const inputCls = 'tl-input w-full';
export const labelCls = 'block text-[11px] font-medium uppercase tracking-wide mb-1';
export const th = 'text-left text-[11px] font-medium uppercase tracking-wide px-3 py-2';
export const td = 'px-3 py-2 text-sm align-top';

export const Pill: React.FC<{ tone: Tone; children: React.ReactNode }> = ({ tone, children }) => (
  <span className={`tl-pill tl-pill-${tone}`}>{children}</span>
);

export const Note: React.FC<{ note: { ok: boolean; text: string } | null }> = ({ note }) =>
  !note ? null : (
    <div className={`text-sm rounded-xl px-3 py-2 flex items-center gap-2 ${note.ok
      ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-700'}`}>
      {note.ok ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
      <span>{note.text}</span>
    </div>
  );

export const Card: React.FC<{ title: string; subtitle?: React.ReactNode; children: React.ReactNode; right?: React.ReactNode }> =
  ({ title, subtitle, children, right }) => (
    <section className="tl-card p-5 space-y-3">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-semibold text-gray-900 dark:text-gray-100">{title}</h2>
          {subtitle && <p className="text-sm mt-0.5" style={muted}>{subtitle}</p>}
        </div>
        {right}
      </div>
      {children}
    </section>
  );

/**
 * CSV import with a mandatory dry run. "Apply" is offered only after a preview of the SAME text,
 * and the preview is thrown away when the text changes — an import nobody has previewed is the
 * one that writes 4,000 wrong rows.
 */
export const ImportPanel: React.FC<{
  columns: string;
  example: string;
  run: (csv: string, dryRun: boolean) => Promise<ApiResponse<ImportResult>>;
  onApplied?: () => void;
  renderPreview?: (r: ImportResult) => React.ReactNode;
}> = ({ columns, example, run, onApplied, renderPreview }) => {
  const [csv, setCsv] = useState('');
  const [preview, setPreview] = useState<ImportResult | null>(null);
  const [previewedText, setPreviewedText] = useState<string | null>(null);
  const [busy, setBusy] = useState<'dry' | 'apply' | null>(null);
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);

  const pick = async (f?: File | null) => {
    if (!f) return;
    try { setCsv(await readFileText(f)); setPreview(null); setPreviewedText(null); }
    catch (e: any) { setNote({ ok: false, text: e?.message || 'Could not read the file' }); }
  };

  const go = async (dryRun: boolean) => {
    setBusy(dryRun ? 'dry' : 'apply'); setNote(null);
    const r = await run(csv, dryRun);
    setBusy(null);
    if (!r.success || !r.data) { setNote({ ok: false, text: r.error || 'Import failed' }); return; }
    if (dryRun) { setPreview(r.data); setPreviewedText(csv); return; }
    setNote({ ok: true, text: `Imported ${r.data.written} row(s); ${r.data.invalid} rejected.` });
    setPreview(null); setPreviewedText(null); setCsv('');
    onApplied?.();
  };

  const canApply = !!preview && previewedText === csv && preview.valid > 0;
  const errors = preview?.errors || (preview?.results || []).filter((x) => x.error);

  return (
    <div className="space-y-3">
      <p className="text-xs" style={muted}>Columns: <code>{columns}</code>. Example: <code>{example}</code></p>
      <div className="flex items-center gap-2 flex-wrap">
        <label className="tl-btn tl-btn-secondary tl-btn-sm cursor-pointer">
          <Upload className="w-3.5 h-3.5" /> Choose CSV
          <input type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => pick(e.target.files?.[0])} />
        </label>
        <span className="text-xs" style={muted}>or paste below</span>
      </div>
      <textarea className={`${inputCls} font-mono text-xs`} rows={5} value={csv}
                onChange={(e) => { setCsv(e.target.value); }} placeholder={`${columns}\n${example}`} />
      <div className="flex gap-2">
        <button type="button" className="tl-btn tl-btn-secondary tl-btn-sm" disabled={!csv.trim() || !!busy} onClick={() => go(true)}>
          {busy === 'dry' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} Preview (dry run)
        </button>
        <button type="button" className="tl-btn tl-btn-primary tl-btn-sm disabled:opacity-50" disabled={!canApply || !!busy} onClick={() => go(false)}
                title={canApply ? '' : 'Preview this exact text first'}>
          {busy === 'apply' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} Apply {preview ? `${preview.valid} row(s)` : ''}
        </button>
      </div>
      <Note note={note} />
      {preview && (
        <div className="space-y-2">
          <p className="text-sm">
            Dry run: <b>{preview.rows}</b> rows, <b>{preview.valid}</b> valid, <b>{preview.invalid}</b> rejected. Nothing was written.
            {previewedText !== csv && <span className="text-amber-700"> The text has changed since — preview again.</span>}
          </p>
          {errors.length > 0 && (
            <ul className="text-xs text-red-700 list-disc pl-5 max-h-40 overflow-auto">
              {errors.slice(0, 50).map((e: any, i: number) => <li key={i}>Line {e.line}: {e.error}</li>)}
            </ul>
          )}
          {renderPreview?.(preview)}
        </div>
      )}
    </div>
  );
};
